import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { uniqBy, isEqual } from "lodash";
import { fetchWithCSRFTokenForRequestObject } from "Utils/fetchWithCSRFToken";

import { appendAuthParams } from "Common/TokenAuth/Utils";

import { PAGE_SIZE, CACHE_DURATION_SECONDS, careApiURL, paginationDefaults } from "Care/core/constants/api";

import type {
  GetProcedureOptionsResponse,
  GetProcedureQuoteParams,
  GetProcedureQuoteResponse,
  GetProcedureQuotesParams,
  GetProcedureQuotesResponse,
  GetProcedureSnapshotQuoteParams,
  GetProcedureSnapshotQuoteResponse,
  GetRelatedCPTCodesParams,
  GetRelatedCPTCodesResponse,
  ProcedureOption,
} from "./quote.types";

export const quoteApi = createApi({
  reducerPath: "quoteApi",
  tagTypes: ["ProcedureOptions", "ProcedureQuotes", "ProcedureQuote", "RelatedCPTCodes", "AnesthesiaQuote", "ImagingQuotes"],
  baseQuery: fetchBaseQuery({
    baseUrl: "/",
    fetchFn: fetchWithCSRFTokenForRequestObject,
  }),
  // cache for 24h - prevents search from clearing stale data
  // RTK@1 doesn't support entirely disabling this behavior, even w/ Infinity
  keepUnusedDataFor: CACHE_DURATION_SECONDS,
  endpoints: (builder) => ({
    getProcedureOptions: builder.query<ProcedureOption[], void>({
      query: () => appendAuthParams(`${careApiURL}/options`),
      transformResponse: (response: GetProcedureOptionsResponse) => response.procedure,
      providesTags: ["ProcedureOptions"],
    }),
    getProcedureSnapshotQuote: builder.query<GetProcedureSnapshotQuoteResponse, GetProcedureSnapshotQuoteParams>({
      query: ({ ...params }) => ({
        url: appendAuthParams(`${careApiURL}/snapshot_quote`),
        method: "POST",
        params: { ...params },
      }),
      providesTags: ["ProcedureQuotes"],
    }),
    getProcedureQuotes: builder.query<GetProcedureQuotesResponse, GetProcedureQuotesParams>({
      query: ({ offset = paginationDefaults.offset, limit = paginationDefaults.limit, ...params }) => ({
        url: appendAuthParams(`${careApiURL}/quote`),
        method: "POST",
        params: { ...params, offset, limit },
      }),
      providesTags: ["ProcedureQuotes"],
      // Exclude pagination params as unique identifiers for this query key
      serializeQueryArgs: ({ queryArgs }) => `${queryArgs.code}-${queryArgs.zip_code}-${queryArgs.order_by}-${queryArgs.radius}`,
      // Changes in offset force a refetch for the entire query (which is merged w/ prior result)
      forceRefetch({ currentArg, previousArg }) {
        return !isEqual(currentArg, previousArg);
      },
      // Query resolution will trigger the next page request if needed
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.pagination) {
            const nextArg = {
              ...arg,
              offset: arg.offset + PAGE_SIZE,
            };
            dispatch(quoteApi.endpoints.getProcedureQuotes.initiate(nextArg));
          }
        } catch (err) {
          console.error("Failed to fetch next page:", err);
        }
      },
      // Results are merged, this is called to transform data when subsequent pages are requested
      merge: (currentData, responseData, { arg: currentArg }) => {
        const quotes = currentData.quotes || [];
        let combinedQuotes = uniqBy([...quotes, ...(responseData.quotes ?? [])], "id");

        combinedQuotes = combinedQuotes.reduce(
          (acc, current) => {
            const uniqueKey = `${current.ein}:${current.entity_name}:${current.entity_address}`;
            if (!acc.tempMap[uniqueKey]) {
              acc.tempMap[uniqueKey] = true; // Mark this key as seen
              acc.uniqueQuotes.push(current); // Add this quote to the result array
            }
            return acc;
          },
          { uniqueQuotes: [], tempMap: {} },
        ).uniqueQuotes;

        return {
          quotes: combinedQuotes,
          coverages: responseData.coverages,
          pagination: responseData.pagination,
          lastParams: { code: currentArg.code, zip_code: currentArg.zip_code },
        };
      },
    }),
    getProcedureQuotesInfinite: builder.query<GetProcedureQuotesResponse, GetProcedureQuotesParams>({
      query: ({ offset = paginationDefaults.offset, limit = paginationDefaults.limit, ...params }) => ({
        url: appendAuthParams(`${careApiURL}/quote`),
        method: "POST",
        params: { ...params, offset, limit },
      }),
      serializeQueryArgs: ({ endpointName }) => endpointName,
      merge: (currentData, responseData, { arg: currentArg }) => {
        const quotes = currentData.quotes || [];
        // Assuming CPT code and zip_code are key parameters for the query
        const shouldResetData = !isEqual(currentData.lastParams, {
          code: currentArg.code,
          zip_code: currentArg.zip_code,
        });

        let combinedQuotes;
        if (shouldResetData) {
          // Reset the quotes array if key search parameters have changed
          combinedQuotes = responseData.quotes ?? [];
        } else {
          // Otherwise, merge with existing quotes
          combinedQuotes = uniqBy([...quotes, ...(responseData.quotes ?? [])], "id");
        }

        combinedQuotes = combinedQuotes.reduce(
          (acc, current) => {
            const uniqueKey = `${current.ein}:${current.entity_name}:${current.entity_address}`;
            if (!acc.tempMap[uniqueKey]) {
              acc.tempMap[uniqueKey] = true; // Mark this key as seen
              acc.uniqueQuotes.push(current); // Add this quote to the result array
            }
            return acc;
          },
          { uniqueQuotes: [], tempMap: {} },
        ).uniqueQuotes;

        return {
          quotes: combinedQuotes,
          coverage: responseData.coverage,
          coverages: responseData.coverages,
          pagination: responseData.pagination,
          lastParams: { code: currentArg.code, zip_code: currentArg.zip_code },
        };
      },
      forceRefetch({ currentArg, previousArg }) {
        return !isEqual(currentArg, previousArg);
      },
      providesTags: ["ProcedureQuotes"],
    }),
    getProcedureQuote: builder.query<GetProcedureQuoteResponse, GetProcedureQuoteParams>({
      query: (params) => ({
        url: appendAuthParams(`${careApiURL}/quote`),
        method: "GET",
        params,
      }),
      providesTags: ["ProcedureQuote"],
    }),
    getRelatedCPTCodes: builder.query<GetRelatedCPTCodesResponse, GetRelatedCPTCodesParams>({
      query: (body) => ({
        url: appendAuthParams(`${careApiURL}/related_cpt_codes_quotes`),
        method: "POST",
        body,
      }),
      providesTags: ["RelatedCPTCodes"],
    }),
    getRelatedAnesthesiaQuote: builder.query({
      query: (body) => ({
        url: appendAuthParams(`${careApiURL}/related_anesthesia_quote`),
        method: "POST",
        body,
      }),
      providesTags: ["AnesthesiaQuote"],
    }),
    getRelatedImagingQuotes: builder.query({
      query: (body) => ({
        url: appendAuthParams(`${careApiURL}/related_imaging_quotes`),
        method: "POST",
        body,
      }),
      providesTags: ["ImagingQuotes"],
    }),
  }),
});

export const {
  useGetProcedureOptionsQuery,
  useGetProcedureSnapshotQuoteQuery,
  useGetProcedureQuotesQuery,
  useGetProcedureQuotesInfiniteQuery,
  useLazyGetProcedureQuotesInfiniteQuery,
  useGetProcedureQuoteQuery,
  useLazyGetProcedureQuotesQuery,
  useGetRelatedCPTCodesQuery,
  useGetRelatedAnesthesiaQuoteQuery,
  useLazyGetRelatedAnesthesiaQuoteQuery,
  useGetRelatedImagingQuotesQuery,
  useLazyGetRelatedImagingQuotesQuery,
} = quoteApi;
